import type { MatchedQuestionSnippetRes, SearchHighlightRes } from '@/api';
import { cn } from '@/utils';
import { HighlightedSearchText } from './HighlightedSearchText';
interface MatchedQuestionsProps {
  snippets: readonly MatchedQuestionSnippetRes[];
  legacyQuestions?: readonly string[];
  legacyHighlights?: readonly SearchHighlightRes[];
  className?: string;
}

/**
 * TODO(search-v3): 구버전 서버 응답(matchedQuestionSnippets 없음) 호환용 폴백.
 * 서버가 먼저 배포되므로 API 응답에는 항상 snippets 가 채워지고, 이 폴백은
 * SSR/dehydrate 된 구형 캐시 데이터 방어용으로만 의미가 있다.
 * 서버 전면 배포 + 안정화 후 legacyQuestions/legacyHighlights props 와 함께 삭제할 것.
 */
const buildLegacySnippets = (questions: readonly string[] = [], highlights: readonly SearchHighlightRes[] = []): MatchedQuestionSnippetRes[] => questions.map((question, index) => ({
  question,
  hitTerms: highlights[index]?.texts.filter(segment => segment.type === 'hit' && segment.value.length > 0).map(segment => segment.value).filter((term, termIndex, terms) => terms.indexOf(term) === termIndex) ?? []
}));

/**
 * MatchedQuestions
 *
 * Renders question-level snippets returned by the search backend. Each snippet owns
 * its own hit terms, so multiple matched questions do not get flattened into one
 * ambiguous text stream.
 *
 * External Platform decisions (per .claude/rules/external-platform-policy.md):
 * - Normal Quiz:               render
 * - LTI instructor:            render
 * - LTI student:               render
 * - Google Classroom teacher:  render
 * - Google Classroom student:  render
 *
 * Rationale: matched questions are plain, read-only text. No multi-student
 * state, team/round mode, or cross-quiz navigation surfaces are introduced.
 */
export const MatchedQuestions = ({
  snippets,
  legacyQuestions,
  legacyHighlights,
  className
}: MatchedQuestionsProps) => {
  const resolvedSnippets = snippets.length > 0 ? snippets : buildLegacySnippets(legacyQuestions, legacyHighlights);
  if (resolvedSnippets.length === 0) return null;
  return <div className={cn('line-clamp-2 break-words text-body-sm-regular text-alternative', className)} data-sentry-component="MatchedQuestions" data-sentry-source-file="MatchedQuestions.tsx">
      {resolvedSnippets.map((snippet, index) => <span key={`${snippet.question}-${index}`}>
          {index > 0 && ' '}
          <HighlightedSearchText text={snippet.question} hitTerms={snippet.hitTerms} />
        </span>)}
    </div>;
};